import React, { useEffect, useRef, useState, useCallback } from 'react';
import supabase from '../config/supabase';
import { SUPABASE_URL } from '../config/database';

const SUPABASE_FUNCTIONS_URL = SUPABASE_URL.replace(/\/$/, '') + '/functions/v1';

async function apiFetch(path, options = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const res = await fetch(`${SUPABASE_FUNCTIONS_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  return res.json();
}

const STATUS_LABELS = {
  active:       { text: 'Connected', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  expired:      { text: 'Token Expired', color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  revoked:      { text: 'Disconnected', color: '#dc2626', bg: '#fff1f2', border: '#fecdd3' },
  not_connected:{ text: 'Not Connected', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
};

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || STATUS_LABELS.not_connected;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      padding: '3px 10px',
      borderRadius: 999,
      fontSize: '0.75rem',
      fontWeight: 700,
      color: s.color,
      background: s.bg,
      border: `1px solid ${s.border}`,
    }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%',
        background: s.color, display: 'inline-block',
      }} />
      {s.text}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function ProbeRow({ label, result }) {
  if (!result) return null;
  const ok = result.ok;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8,
      padding: '6px 0', borderBottom: '1px solid #F0E8DF',
      fontSize: '0.82rem',
    }}>
      <span style={{ minWidth: 160, color: '#8B7B73', fontWeight: 600, flexShrink: 0 }}>{label}</span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontWeight: 700, color: ok ? '#16a34a' : '#dc2626',
      }}>
        {ok ? '✓' : '✗'}
        {' '}HTTP {result.status || 0}
        {result.error && (
          <span style={{ fontWeight: 400, color: '#8B7B73', marginLeft: 4, wordBreak: 'break-all' }}>
            — {result.error}
          </span>
        )}
      </span>
    </div>
  );
}

function ScopeRow({ scope, granted, requested }) {
  if (!requested) return null;
  const known = granted !== null;
  const ok = !known || granted;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '3px 0', fontSize: '0.82rem',
    }}>
      <span style={{
        fontFamily: 'monospace', background: '#F7F3EF',
        padding: '1px 6px', borderRadius: 4, color: '#5A4A42',
        border: '1px solid #E8DDD4',
      }}>{scope}</span>
      {known ? (
        ok
          ? <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ granted</span>
          : <span style={{ color: '#dc2626', fontWeight: 700 }}>✗ not granted</span>
      ) : (
        <span style={{ color: '#8B7B73' }}>requested (Clover did not return grant list)</span>
      )}
    </div>
  );
}

function DiagnosticsPanel({ storeId }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [diagError, setDiagError] = useState('');

  const runDiagnostics = async () => {
    setRunning(true);
    setDiagError('');
    setResult(null);
    try {
      const data = await apiFetch('/clover-diagnostics', {
        method: 'POST',
        body: JSON.stringify({ store_id: storeId }),
      });
      if (data.error) throw new Error(data.error);
      setResult(data);
    } catch (err) {
      setDiagError(err.message || 'Diagnostics failed.');
    } finally {
      setRunning(false);
    }
  };

  const sectionLabel = {
    fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.5px', color: '#A89890',
    margin: '14px 0 6px', paddingBottom: 4,
    borderBottom: '1px solid #F0E8DF',
  };

  const rowStyle = {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    fontSize: '0.82rem', color: '#5A4A42',
    padding: '5px 0', borderBottom: '1px solid #F0E8DF',
  };

  const labelStyle = {
    color: '#8B7B73', minWidth: 160, fontWeight: 600, flexShrink: 0,
  };

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <button
          onClick={runDiagnostics}
          disabled={running}
          style={{
            padding: '7px 16px', borderRadius: 8,
            border: '1px solid #D4C4B0', background: running ? '#F7F3EF' : '#FAF6F2',
            color: '#5A4A42', fontWeight: 700,
            fontSize: '0.82rem', cursor: running ? 'not-allowed' : 'pointer',
            opacity: running ? 0.7 : 1,
          }}
        >
          {running ? '⏳ Running…' : '🔍 Run diagnostics'}
        </button>
        {result && !running && (
          <span style={{ fontSize: '0.75rem', color: '#A89890' }}>
            Completed {new Date().toLocaleTimeString()}
          </span>
        )}
      </div>

      {diagError && (
        <div style={{
          background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8,
          padding: '8px 12px', color: '#dc2626', fontSize: '0.82rem', marginTop: 6,
        }}>
          {diagError}
        </div>
      )}

      {result && (
        <div style={{
          background: '#FEFCFA', border: '1px solid #E8DDD4', borderRadius: 8,
          padding: '14px 16px', marginTop: 8,
        }}>
          <div style={sectionLabel}>Environment</div>
          {[
            ['Environment', result.environment === 'sandbox'
              ? '🟡 Sandbox (test mode)'
              : '🟢 Production'],
            ['OAuth base URL', result.oauth_base_url],
            ['API base URL', result.api_base_url],
            ['App ID (last 6)', result.app_id],
            ['Merchant ID', result.merchant_id || '—'],
            ['Token health', result.token_ok
              ? '✓ Valid'
              : '✗ Invalid or expired'],
            ['Token expires', fmtDate(result.token_expires_at)],
          ].map(([label, value]) => (
            <div key={label} style={rowStyle}>
              <span style={labelStyle}>{label}</span>
              <span style={{ wordBreak: 'break-all' }}>{value}</span>
            </div>
          ))}

          <div style={sectionLabel}>Permissions</div>

          {result.scope_mismatch && (
            <div style={{
              background: '#FFF8E1', border: '1px solid #F9CA4A', borderRadius: 6,
              padding: '8px 12px', marginBottom: 8, fontSize: '0.82rem', color: '#7B5A00',
            }}>
              <strong>Scope mismatch detected.</strong> Clover granted fewer permissions than requested.
              Reconnect to re-consent with the current permission set.
            </div>
          )}

          {result.employees_r_requested && result.employees_r_granted === false && (
            <div style={{
              background: '#FDF0EF', border: '1px solid #F2C5C1', borderRadius: 6,
              padding: '8px 12px', marginBottom: 8, fontSize: '0.82rem', color: '#8B2317',
            }}>
              <strong>Employees permission not granted.</strong> The Employees permission was
              requested but Clover did not grant it. In the Clover Developer Dashboard, enable
              "Employees: Read" for this app, then use <em>Reconnect</em> so the merchant can
              re-consent with the new permission set.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 8 }}>
            {(result.requested_scopes || []).map((scope) => (
              <ScopeRow
                key={scope}
                scope={scope}
                requested={true}
                granted={result.granted_scopes
                  ? result.granted_scopes.includes(scope)
                  : null}
              />
            ))}
          </div>

          <div style={sectionLabel}>Live API Probes</div>

          {!result.token_ok && (
            <div style={{
              fontSize: '0.80rem', color: '#8B7B73', marginBottom: 6,
              fontStyle: 'italic',
            }}>
              No valid token — probes show the last known state only.
            </div>
          )}

          <ProbeRow label="Merchant profile" result={result.merchant_probe} />
          <ProbeRow label="Employees endpoint" result={result.employees_probe} />

          {result.employees_probe && !result.employees_probe.ok && (
            <div style={{
              marginTop: 8, fontSize: '0.80rem', color: '#8B7B73',
              lineHeight: 1.5,
            }}>
              {result.employees_probe.status === 401 || result.employees_probe.status === 403
                ? 'HTTP ' + result.employees_probe.status + ' on employees — the token is missing the "Employees: Read" (EMPLOYEES_R) permission. Enable it in the Clover Developer Dashboard, then reconnect.'
                : result.employees_probe.status === 0
                  ? 'Could not reach Clover — check that the API base URL matches the environment where this merchant account was created.'
                  : 'Employees endpoint returned an unexpected error. Check the error text above.'}
            </div>
          )}

          {result.merchant_probe?.ok === false && (result.merchant_probe?.status === 0 || result.merchant_probe?.status === 401 || result.merchant_probe?.status === 403) && (
            <div style={{
              marginTop: 8, fontSize: '0.80rem', color: '#7B5A00',
              lineHeight: 1.5, background: '#FFF8E1',
              border: '1px solid #F9CA4A', borderRadius: 6, padding: '8px 12px',
            }}>
              <strong>Wrong environment?</strong>{' '}
              {result.environment === 'sandbox'
                ? 'The edge functions are configured for Sandbox. If this merchant connected via Production, the token will not work.'
                : 'The edge functions are configured for Production. If this merchant connected via Sandbox, the token will not work.'
              }{' '}
              Confirm your CLOVER_OAUTH_BASE_URL and CLOVER_API_BASE_URL secrets match
              the environment where the merchant account was created, then reconnect.
            </div>
          )}

          {result.employees && (
            <>
              <div style={sectionLabel}>Employees</div>

              {[
                ['Fetch status', result.employees.fetch_ok
                  ? '✓ HTTP ' + (result.employees.fetch_status || 0)
                  : '✗ HTTP ' + (result.employees.fetch_status || 0)],
                ['Returned (raw)', String(result.employees.raw_count ?? 0)],
                ['Shown (non-deleted)', String(result.employees.shown_count ?? 0)],
                ['Employees: Read scope', result.employees.employees_r_granted === true
                  ? '✓ granted'
                  : result.employees.employees_r_granted === false
                    ? '✗ not granted'
                    : 'unknown'],
              ].map(([label, value]) => (
                <div key={label} style={rowStyle}>
                  <span style={labelStyle}>{label}</span>
                  <span style={{ wordBreak: 'break-all' }}>{value}</span>
                </div>
              ))}

              {result.employees.summary && (
                <div style={{
                  marginTop: 8, fontSize: '0.80rem', color: '#5A4A42',
                  lineHeight: 1.5, background: '#FAF6F2',
                  border: '1px solid #E8DDD4', borderRadius: 6, padding: '8px 12px',
                }}>
                  <strong>Summary:</strong> {result.employees.summary}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const CloverSettings = () => {
  const [storeId, setStoreId] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  const loadStatus = useCallback(async (sid, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const data = await apiFetch(`/clover-status?store_id=${sid}`);
      if (data.error) throw new Error(data.error);
      setStatus(data);
    } catch (err) {
      if (!silent) setError(err.message || 'Could not load Clover connection status.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let sid = Number(localStorage.getItem('selectedStoreId'));
    if (!sid) {
      setError('No store selected.');
      setLoading(false);
      return;
    }
    setStoreId(sid);

    // Check for redirect params from OAuth callback
    const params = new URLSearchParams(window.location.search);

    // Restore store context from callback URL (set by clover-oauth-callback redirect)
    // This ensures the correct store is active even if localStorage was cleared
    // while the user was on the Clover OAuth consent screen.
    const urlStoreId = Number(params.get('store_id'));
    if (urlStoreId > 0) {
      localStorage.setItem('selectedStoreId', String(urlStoreId));
      sid = urlStoreId;
      setStoreId(urlStoreId);
    }

    if (params.get('clover_connected') === '1') {
      setSuccessMsg('Clover connected successfully!');
      // Clean the URL
      const url = new URL(window.location.href);
      url.searchParams.delete('clover_connected');
      url.searchParams.delete('store_id');
      window.history.replaceState({}, '', url.toString());
    }
    const cloverErr = params.get('clover_error');
    if (cloverErr) {
      const messages = {
        invalid_state: 'OAuth state was invalid or expired. Please try again.',
        token_exchange_failed: 'Could not exchange the Clover authorization code. Please try again.',
        no_merchant_id: 'Clover did not return a merchant ID. Please contact support.',
        encryption_failed: 'Server configuration error. Please contact support.',
        db_error: 'Could not save the connection. Please try again.',
        missing_params: 'Incomplete OAuth callback. Please try connecting again.',
        merchant_already_linked: 'This Clover merchant is already linked to another Countertop store. Disconnect it there first.',
      };
      setError(messages[cloverErr] || `Connection failed: ${cloverErr}`);
      const url2 = new URL(window.location.href);
      url2.searchParams.delete('clover_error');
      window.history.replaceState({}, '', url2.toString());
    }

    loadStatus(sid);
  }, [loadStatus]);

  // ── Background polling (30s interval, paused when tab hidden) ─────────────
  const pollStoreIdRef = useRef(null);
  useEffect(() => {
    if (!storeId) return;
    pollStoreIdRef.current = storeId;

    const POLL_INTERVAL_MS = 30_000;
    let timerId;

    function scheduleNext() {
      timerId = setTimeout(async () => {
        if (!document.hidden && pollStoreIdRef.current) {
          await loadStatus(pollStoreIdRef.current, { silent: true });
        }
        scheduleNext();
      }, POLL_INTERVAL_MS);
    }

    scheduleNext();
    return () => clearTimeout(timerId);
  }, [storeId, loadStatus]);

  const handleConnect = async () => {
    if (!storeId) return;
    setConnecting(true);
    setError('');
    try {
      const data = await apiFetch(`/clover-oauth-start?store_id=${storeId}`);
      if (data.error) throw new Error(data.error);
      if (!data.authUrl) throw new Error('No OAuth URL returned.');
      window.location.href = data.authUrl;
    } catch (err) {
      setError(err.message || 'Could not start Clover connection.');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!storeId) return;
    if (!window.confirm(
      'Disconnect Clover? This will stop all Clover data syncs for this store. You can reconnect at any time.',
    )) return;

    setDisconnecting(true);
    setError('');
    try {
      const data = await apiFetch('/clover-status', {
        method: 'POST',
        body: JSON.stringify({ action: 'disconnect', store_id: storeId }),
      });
      if (data.error) throw new Error(data.error);
      setSuccessMsg('Clover disconnected.');
      await loadStatus(storeId);
    } catch (err) {
      setError(err.message || 'Could not disconnect.');
    } finally {
      setDisconnecting(false);
    }
  };

  const isConnected = status?.connected && status?.status === 'active';
  const isExpired   = status?.status === 'expired';
  const isRevoked   = status?.status === 'revoked' || status?.status === 'not_connected';

  const sectionStyle = {
    fontFamily: "'Montserrat', sans-serif",
  };

  const rowStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    fontSize: '0.82rem',
    color: '#5A4A42',
    padding: '5px 0',
    borderBottom: '1px solid #F0E8DF',
  };

  const labelStyle = {
    color: '#8B7B73',
    minWidth: 150,
    fontWeight: 600,
    flexShrink: 0,
  };

  return (
    <div style={sectionStyle}>
      {successMsg && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
          padding: '10px 14px', marginBottom: 14, color: '#16a34a', fontSize: '0.85rem', fontWeight: 600,
        }}>
          ✓ {successMsg}
        </div>
      )}

      {error && (
        <div style={{
          background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8,
          padding: '10px 14px', marginBottom: 14, color: '#dc2626', fontSize: '0.85rem',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: '#8B7B73', fontSize: '0.85rem', padding: '12px 0' }}>
          Loading connection status…
        </div>
      ) : (
        <>
          {/* Status row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
            <StatusBadge status={status?.status || 'not_connected'} />
            {isConnected && status.merchant_id && (
              <span style={{ fontSize: '0.82rem', color: '#5A4A42' }}>
                Merchant <strong>{status.merchant_id}</strong>
              </span>
            )}
          </div>

          {/* Expired warning banner */}
          {isExpired && (
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
              padding: '10px 14px', marginBottom: 14, color: '#92400e', fontSize: '0.82rem',
            }}>
              <strong>Token expired.</strong> Clover data syncs are paused. Reconnect below to resume.
            </div>
          )}

          {/* Details table */}
          {status && status.status !== 'not_connected' && (
            <div style={{ marginBottom: 20 }}>
              {[
                ['Connected at',       fmtDate(status.connected_at)],
                ['Last refreshed',     fmtDate(status.last_refreshed_at)],
                ['Last API success',   fmtDate(status.last_api_success_at)],
                ['Last API error',     status.last_api_error || '—'],
                ['Token expires',      fmtDate(status.token_expires_at)],
              ].map(([label, value]) => (
                <div key={label} style={rowStyle}>
                  <span style={labelStyle}>{label}</span>
                  <span style={{ wordBreak: 'break-all' }}>{value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
            {(isRevoked || !status || status.status === 'not_connected') && (
              <button
                onClick={handleConnect}
                disabled={connecting}
                style={{
                  padding: '9px 20px', borderRadius: 8, border: 'none',
                  background: '#5A4A42', color: '#fff', fontWeight: 700,
                  fontSize: '0.85rem', cursor: connecting ? 'not-allowed' : 'pointer',
                  opacity: connecting ? 0.7 : 1,
                }}
              >
                {connecting ? 'Redirecting to Clover…' : 'Connect to Clover'}
              </button>
            )}

            {isConnected && (
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                style={{
                  padding: '9px 20px', borderRadius: 8,
                  border: '1px solid #F5C6BF', background: '#FFF5F5',
                  color: '#C0392B', fontWeight: 700,
                  fontSize: '0.85rem', cursor: disconnecting ? 'not-allowed' : 'pointer',
                  opacity: disconnecting ? 0.7 : 1,
                }}
              >
                {disconnecting ? 'Disconnecting…' : 'Disconnect'}
              </button>
            )}

            {(isExpired || isConnected) && (
              <button
                onClick={handleConnect}
                disabled={connecting}
                style={{
                  padding: '9px 20px', borderRadius: 8,
                  border: '1px solid #D4C4B0', background: '#FAF6F2',
                  color: '#5A4A42', fontWeight: 600,
                  fontSize: '0.85rem', cursor: connecting ? 'not-allowed' : 'pointer',
                  opacity: connecting ? 0.7 : 1,
                }}
              >
                {connecting ? 'Redirecting…' : 'Reconnect'}
              </button>
            )}

            <button
              onClick={() => loadStatus(storeId)}
              disabled={loading}
              style={{
                padding: '9px 14px', borderRadius: 8,
                border: '1px solid #D4C4B0', background: 'transparent',
                color: '#8B7B73', fontWeight: 600,
                fontSize: '0.82rem', cursor: 'pointer',
              }}
            >
              ↻ Refresh
            </button>
          </div>

          {/* Help text */}
          {(!status || status.status === 'not_connected') && (
            <p style={{
              marginTop: 18, fontSize: '0.80rem', color: '#A89890', lineHeight: 1.6,
            }}>
              Connect your Clover account to sync your item catalog and sales data into Countertop.
              You'll be redirected to Clover to authorize the connection and then brought back here.
            </p>
          )}

          {/* Reconnect note */}
          {(isConnected || isExpired) && (
            <p style={{
              marginTop: 14, fontSize: '0.78rem', color: '#A89890', lineHeight: 1.5,
            }}>
              If you recently added new permissions to your Clover app (e.g. Employees: Read),
              use <strong>Reconnect</strong> — it will re-show the Clover consent screen so the
              new permissions take effect.
            </p>
          )}

          {/* Diagnostics section (store owner / super-admin) */}
          {storeId && status && status.status !== 'not_connected' && (
            <div style={{ marginTop: 24, borderTop: '1px solid #F0E8DF', paddingTop: 16 }}>
              <button
                onClick={() => setShowDiagnostics((v) => !v)}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  fontSize: '0.82rem', color: '#8B7B73', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  textDecoration: 'underline',
                }}
              >
                {showDiagnostics ? '▲ Hide diagnostics' : '▼ Diagnostics'}
              </button>

              {showDiagnostics && (
                <DiagnosticsPanel storeId={storeId} />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CloverSettings;
